// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  features/dashboard/catalogo.ts
//
//  Catálogo estático de widgets do dashboard. Cada widget declara:
//   - id estável (persistido no layout jsonb — NUNCA renomear);
//   - formularioId cuja permissão de LEITURA libera ver/adicionar o widget
//     (null = visível para qualquer usuário autenticado);
//   - tamanho padrão/mínimo em células do grid (12 colunas).
//  Novos widgets (Fase 2) entram aqui + componente correspondente.
// ════════════════════════════════════════════════════════════════════════════

import type { ComponentType } from 'react';
import { FORMULARIOS } from '@/domain/formularios';
import { DashboardStats } from '@/components/DashboardStats';
import { TopProducts } from '@/components/TopProducts';
import { StockAlerts } from '@/components/StockAlerts';
import { QuickActions } from '@/components/QuickActions';
import {
  AgendaHojeWidget, ContasVencerWidget, ContratosWidget, CrmPipelineWidget,
  FiscalEmissaoWidget, FluxoProjetadoWidget, InadimplenciaWidget,
  OsResumoWidget, VendasDiaWidget,
} from './widgets';
import type { WidgetPos, WidgetProps } from './types';

export interface WidgetDef {
  id: string;
  titulo: string;
  descricao: string;
  categoria: 'Financeiro' | 'Vendas' | 'Estoque' | 'Geral' | 'Serviços' | 'Agenda' | 'Fiscal' | 'CRM';
  /** Permissão de leitura exigida (null = sempre disponível). */
  formularioId: number | null;
  /** Tamanho padrão ao adicionar (grid de 12 colunas). */
  w: number;
  h: number;
  minW: number;
  minH: number;
  componente: ComponentType<WidgetProps>;
}

export const CATALOGO_WIDGETS: WidgetDef[] = [
  {
    id: 'financeiro.resumo',
    titulo: 'Resumo Financeiro',
    descricao: 'A receber, a pagar, recebido e saldo previsto.',
    categoria: 'Financeiro',
    formularioId: FORMULARIOS.CONTAS_RECEBER,
    w: 12, h: 2, minW: 6, minH: 2,
    componente: DashboardStats,
  },
  {
    id: 'vendas.top-itens',
    titulo: 'Mais Vendidos Hoje',
    descricao: 'Produtos e serviços mais vendidos no dia.',
    categoria: 'Vendas',
    formularioId: FORMULARIOS.RELATORIOS_VENDAS,
    w: 8, h: 6, minW: 4, minH: 4,
    componente: TopProducts,
  },
  {
    id: 'geral.acoes-rapidas',
    titulo: 'Ações Rápidas',
    descricao: 'Atalhos para as funções mais usadas.',
    categoria: 'Geral',
    formularioId: null,
    w: 4, h: 4, minW: 3, minH: 3,
    componente: QuickActions,
  },
  {
    id: 'estoque.alertas',
    titulo: 'Alertas de Estoque',
    descricao: 'Produtos abaixo do estoque mínimo.',
    categoria: 'Estoque',
    formularioId: FORMULARIOS.ESTOQUE_REPOSICAO,
    w: 12, h: 6, minW: 4, minH: 3,
    componente: StockAlerts,
  },

  // ── Fase 2 — widgets de dados agregados ─────────────────────────────────
  {
    id: 'financeiro.contas-vencer',
    titulo: 'Contas a Vencer',
    descricao: 'Títulos em aberto vencendo hoje e nos próximos 7 dias.',
    categoria: 'Financeiro',
    formularioId: FORMULARIOS.FLUXO_CAIXA,
    w: 4, h: 4, minW: 3, minH: 3,
    componente: ContasVencerWidget,
  },
  {
    id: 'financeiro.inadimplencia',
    titulo: 'Inadimplência',
    descricao: 'Recebíveis vencidos e percentual da carteira.',
    categoria: 'Financeiro',
    formularioId: FORMULARIOS.CONTAS_RECEBER,
    w: 4, h: 4, minW: 3, minH: 3,
    componente: InadimplenciaWidget,
  },
  {
    id: 'financeiro.fluxo-projetado',
    titulo: 'Fluxo Projetado',
    descricao: 'Entradas × saídas previstas nos próximos 30 dias.',
    categoria: 'Financeiro',
    formularioId: FORMULARIOS.FLUXO_CAIXA,
    w: 4, h: 4, minW: 3, minH: 3,
    componente: FluxoProjetadoWidget,
  },
  {
    id: 'contratos.resumo',
    titulo: 'Contratos',
    descricao: 'Contratos ativos e vencimentos em 30 dias.',
    categoria: 'Serviços',
    formularioId: FORMULARIOS.CONTRATOS,
    w: 4, h: 4, minW: 3, minH: 3,
    componente: ContratosWidget,
  },
  {
    id: 'agenda.hoje',
    titulo: 'Agenda de Hoje',
    descricao: 'Seus compromissos do dia (atividades CRM).',
    categoria: 'Agenda',
    formularioId: FORMULARIOS.CRM,
    w: 4, h: 6, minW: 3, minH: 4,
    componente: AgendaHojeWidget,
  },
  {
    id: 'os.resumo',
    titulo: 'Ordens de Serviço',
    descricao: 'OS abertas, emitidas e concluídas no mês.',
    categoria: 'Serviços',
    formularioId: FORMULARIOS.ORDEM_SERVICO,
    w: 4, h: 3, minW: 3, minH: 3,
    componente: OsResumoWidget,
  },
  {
    id: 'vendas.resumo-dia',
    titulo: 'Vendas do Dia',
    descricao: 'Quantidade, total e ticket médio de hoje.',
    categoria: 'Vendas',
    formularioId: FORMULARIOS.RELATORIOS_VENDAS,
    w: 4, h: 3, minW: 3, minH: 3,
    componente: VendasDiaWidget,
  },
  {
    id: 'fiscal.resumo-emissao',
    titulo: 'Emissão Fiscal',
    descricao: 'NF-e/NFC-e emitidas no mês corrente.',
    categoria: 'Fiscal',
    formularioId: FORMULARIOS.EMISSAO_NFE,
    w: 4, h: 4, minW: 3, minH: 3,
    componente: FiscalEmissaoWidget,
  },
  {
    id: 'crm.pipeline',
    titulo: 'Pipeline CRM',
    descricao: 'Valor do funil, oportunidades abertas e conversão.',
    categoria: 'CRM',
    formularioId: FORMULARIOS.CRM_DASHBOARD,
    w: 4, h: 4, minW: 3, minH: 3,
    componente: CrmPipelineWidget,
  },
];

// ── Templates de segmento ("Começar de um modelo") ─────────────────────────

export interface TemplateSegmento {
  id: string;
  nome: string;
  descricao: string;
  widgets: WidgetPos[];
}

export const TEMPLATES_SEGMENTO: TemplateSegmento[] = [
  {
    id: 'comercio',
    nome: 'Comércio / Varejo',
    descricao: 'Vendas do dia, produtos mais vendidos, estoque e fiscal.',
    widgets: [
      { id: 'financeiro.resumo',      x: 0, y: 0,  w: 12, h: 2 },
      { id: 'vendas.resumo-dia',      x: 0, y: 2,  w: 4,  h: 3 },
      { id: 'geral.acoes-rapidas',    x: 0, y: 5,  w: 4,  h: 4 },
      { id: 'vendas.top-itens',       x: 4, y: 2,  w: 8,  h: 6 },
      { id: 'fiscal.resumo-emissao',  x: 8, y: 8,  w: 4,  h: 4 },
      { id: 'estoque.alertas',        x: 0, y: 9,  w: 8,  h: 5 },
    ],
  },
  {
    id: 'servicos',
    nome: 'Prestação de Serviços',
    descricao: 'Contratos, agenda, OS, contas a vencer e inadimplência.',
    widgets: [
      { id: 'financeiro.resumo',        x: 0, y: 0, w: 12, h: 2 },
      { id: 'contratos.resumo',         x: 0, y: 2, w: 4,  h: 4 },
      { id: 'financeiro.contas-vencer', x: 4, y: 2, w: 4,  h: 4 },
      { id: 'financeiro.inadimplencia', x: 8, y: 2, w: 4,  h: 4 },
      { id: 'agenda.hoje',              x: 0, y: 6, w: 4,  h: 6 },
      { id: 'os.resumo',                x: 4, y: 6, w: 4,  h: 3 },
      { id: 'geral.acoes-rapidas',      x: 8, y: 6, w: 4,  h: 4 },
    ],
  },
  {
    id: 'educacao',
    nome: 'Educação / Recorrência',
    descricao: 'Matrículas (contratos), inadimplência, agenda e financeiro.',
    widgets: [
      { id: 'financeiro.resumo',        x: 0, y: 0, w: 12, h: 2 },
      { id: 'contratos.resumo',         x: 0, y: 2, w: 4,  h: 4 },
      { id: 'financeiro.inadimplencia', x: 4, y: 2, w: 4,  h: 4 },
      { id: 'financeiro.contas-vencer', x: 8, y: 2, w: 4,  h: 4 },
      { id: 'agenda.hoje',              x: 0, y: 6, w: 4,  h: 6 },
      { id: 'geral.acoes-rapidas',      x: 4, y: 6, w: 4,  h: 4 },
    ],
  },
  {
    id: 'industria',
    nome: 'Indústria / Produção',
    descricao: 'Estoque, reposição, fluxo projetado e vendas.',
    widgets: [
      { id: 'financeiro.resumo',           x: 0, y: 0, w: 12, h: 2 },
      { id: 'financeiro.fluxo-projetado',  x: 0, y: 2, w: 4,  h: 4 },
      { id: 'vendas.top-itens',            x: 4, y: 2, w: 8,  h: 6 },
      { id: 'os.resumo',                   x: 0, y: 6, w: 4,  h: 3 },
      { id: 'estoque.alertas',             x: 0, y: 9, w: 12, h: 5 },
    ],
  },
];

export const widgetPorId = (id: string): WidgetDef | undefined =>
  CATALOGO_WIDGETS.find((w) => w.id === id);

/** Template default (arranjo da tela original) — usado quando não há layout salvo. */
export const LAYOUT_PADRAO: WidgetPos[] = [
  { id: 'financeiro.resumo',   x: 0, y: 0, w: 12, h: 2 },
  { id: 'vendas.top-itens',    x: 0, y: 2, w: 8,  h: 6 },
  { id: 'geral.acoes-rapidas', x: 8, y: 2, w: 4,  h: 4 },
  { id: 'estoque.alertas',     x: 0, y: 8, w: 12, h: 6 },
];
